import {
	OnChangeFn,
	PaginationState,
	SortingState,
	Updater,
	flexRender,
	getCoreRowModel,
	useReactTable,
} from '@tanstack/react-table';
import { useMemo } from 'react';
import sortIcon from '../assets/icons/sort.svg';
import ascIcon from '../assets/icons/asc.svg';
import descIcon from '../assets/icons/desc.svg';
import { page_size_options } from '../constants';

/*Type of rows and columns changes dynamically so there are set to any.*/
type TableProps = {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	rows: any[];
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	columns: any[];
	rowCount?: number;
	sorting?: SortingState | [];
	pagination?: PaginationState;
	handleSorting?: (sorting: SortingState) => void;
	handlePagination?: (pagination: PaginationState) => void;
	widthClass?: string;
	heightClass?: string;
	containerClass?: string;
	isHeaderSticky?: boolean;
	isPagesizeRequired?: boolean;
};
const GridTable = ({
	rows,
	columns,
	sorting,
	pagination,
	handlePagination,
	handleSorting,
	rowCount,
	widthClass,
	heightClass,
	isHeaderSticky,
	containerClass,
	isPagesizeRequired: isPagesizeRequired = true,
}: TableProps) => {
	const _setSorting: OnChangeFn<SortingState> = (
		updaterOrValue: Updater<SortingState>,
	) => {
		const sort =
			typeof updaterOrValue === 'function'
				? updaterOrValue(sorting!)
				: updaterOrValue;

		handleSorting?.(sort);
	};

	const _setPagination: OnChangeFn<PaginationState> = (
		updaterOrValue: Updater<PaginationState>,
	) => {
		const _pagination =
			typeof updaterOrValue === 'function'
				? updaterOrValue(pagination!)
				: updaterOrValue;
		handlePagination?.(_pagination);
	};

	const table = useReactTable({
		data: rows,
		columns: columns,
		rowCount: rowCount,
		getCoreRowModel: getCoreRowModel(),
		state: {
			sorting,
			pagination,
		},
		defaultColumn: {
			size: 300,
			maxSize: 400,
			minSize: 200,
		},
		manualPagination: true,
		manualSorting: true,
		onSortingChange: _setSorting,
		onPaginationChange: _setPagination,
	});
	const startPage = Math.max(0, table.getState()?.pagination?.pageIndex - 2);
	const endPage = Math.min(
		startPage + 3,
		(pagination ? table.getPageCount() : 0) - 1,
	);
	// Generate an array of page numbers to display
	const pageNumbers = useMemo(() => {
		const numbers = [];
		for (let i = startPage; i <= endPage; i++) {
			numbers.push(i);
		}
		return numbers;
	}, [startPage, endPage]);

	return (
		<div
			className={`flex flex-col justify-between bg-white rounded-lg ${containerClass ? containerClass : ''}`}
		>
			<table
				className={`table-fixed ${heightClass ? heightClass : 'h-[calc(100vh-300px)]'} ${widthClass ? widthClass : 'min-w-[calc(100vw-200px)]'} overflow-auto flex flex-col`}
			>
				<thead
					className={`flex ${isHeaderSticky ? 'z-10 sticky top-[-1px] bg-white min-w-max' : ''}`}
				>
					{table.getHeaderGroups().map((headerGroup) => (
						<tr
							key={headerGroup.id}
							className="flex flex-grow text-left p-2 border-b-2 border-rcrm-primary-border-blue"
						>
							{headerGroup.headers.map((header) => (
								<th
									key={header.id}
									colSpan={header.colSpan}
									className="flex gap-x-1 items-center py-1 px-2 text-rcrm-primary-blue min-w-[100px] max-w-[400px] relative"
								>
									<span className="flex">
										{flexRender(
											header.column.columnDef.header,
											header.getContext(),
										)}
										<span className="absolute right-0 top-3">
											{header.column.getCanSort() && (
												<img
													src={
														(header.column.getIsSorted() as string) ===
														'asc'
															? ascIcon
															: (header.column.getIsSorted() as string) ===
																  'desc'
																? descIcon
																: sortIcon
													}
													className="cursor-pointer"
													onClick={header.column.getToggleSortingHandler()}
													alt="sort"
												/>
											)}
										</span>
									</span>
								</th>
							))}
						</tr>
					))}
				</thead>
				<tbody className="grid">
					{table.getRowModel()?.rows?.length > 0 ? (
						table.getRowModel().rows.map((row) => {
							return (
								<tr
									key={row.id}
									className="flex text-left p-2 border-b-2 border-rcrm-primary-border-blue"
								>
									{row.getVisibleCells().map((cell) => (
										<td
											key={cell.id}
											className="flex gap-x-3 text-sm  items-center py-1 px-2 text-ellipsis indent-1"
										>
											{flexRender(
												cell.column.columnDef.cell,
												cell.getContext(),
											)}
										</td>
									))}
								</tr>
							);
						})
					) : (
						<div className="flex mt-16 justify-center">
							<div>No records to display</div>
						</div>
					)}
				</tbody>
			</table>
			{pagination &&
			Math.ceil(table.getRowCount() / pagination.pageSize) ? (
				<div
					className={`flex items-center px-4 gap-x-2 border-t-2 py-6 border-rcrm-primary-border-blue ${isPagesizeRequired ? 'justify-between' : 'justify-center'}`}
				>
					{isPagesizeRequired ? (
						<div>
							<select
								className="px-1 py-2 border-2 rounded border-rcrm-primary-border-blue outline-none"
								value={table.getState().pagination.pageSize}
								onChange={(e) => {
									table.setPageSize(Number(e.target.value));
								}}
							>
								{page_size_options.map((pageSize) => (
									<option
										key={pageSize}
										value={pageSize}
										className="font-semibold"
									>
										{pageSize}
									</option>
								))}
							</select>
							<span className="font-semibold">{` Items in page ${pagination!.pageIndex + 1} of ${Math.ceil(table.getRowCount() / pagination.pageSize)} `}</span>
						</div>
					) : null}
					<div className="flex items-center">
						<button
							className={`border rounded mx-3 px-2 py-1 ${!table.getCanPreviousPage() && 'text-gray-200'}`}
							onClick={() => table.firstPage()}
							disabled={!table.getCanPreviousPage()}
						>
							{'<<'}
						</button>
						<button
							className={`p-1 ${!table.getCanPreviousPage() && 'text-gray-200'}`}
							onClick={() => table.previousPage()}
							disabled={!table.getCanPreviousPage()}
						>
							<span>{'< '}</span>
							<span className="underline">Prev</span>
						</button>

						<div className="mx-3">
							{pageNumbers?.map((number) => (
								<button
									key={number}
									onClick={() => table.setPageIndex(number)}
									className={`w-12 px-3 py-1 font-bold rounded ${table.getState().pagination.pageIndex === number ? 'bg-rcrm-primary-bg ' : ''}`}
								>
									{number + 1}
								</button>
							))}
						</div>
						<button
							className={`p-1 ${!table.getCanNextPage() && 'text-gray-200'}`}
							onClick={() => table.nextPage()}
							disabled={!table.getCanNextPage()}
						>
							<span className="underline">Next</span>
							<span>{' >'}</span>
						</button>
						<button
							className={`border rounded mx-3 px-2 py-1 ${!table.getCanNextPage() && 'text-gray-200'}`}
							onClick={() => table.lastPage()}
							disabled={!table.getCanNextPage()}
						>
							{'>>'}
						</button>
					</div>
				</div>
			) : null}
		</div>
	);
};
export default GridTable;
